import { supabase } from '@/lib/supabase';
import type { LessonOverride, LessonOverrideKind } from '@/types';

export interface OverrideInput {
  studentId: string;
  /** 이 수업의 원래 날짜 */
  date: string;
  kind: LessonOverrideKind;
  start?: string;
  end?: string;
  movedToDate?: string | null;
  note?: string;
}

/** 기간 내(원래 날짜 또는 이동 도착일이 기간에 걸친) 수업 변경을 불러와요. */
export async function fetchOverridesForRange(
  fromDate: string,
  toDate: string,
): Promise<LessonOverride[]> {
  const [outRes, inRes] = await Promise.all([
    supabase
      .from('lesson_overrides')
      .select('*')
      .gte('override_date', fromDate)
      .lte('override_date', toDate),
    supabase
      .from('lesson_overrides')
      .select('*')
      .gte('moved_to_date', fromDate)
      .lte('moved_to_date', toDate),
  ]);
  if (outRes.error) throw outRes.error;
  if (inRes.error) throw inRes.error;
  const map = new Map<string, LessonOverride>();
  ([...(outRes.data ?? []), ...(inRes.data ?? [])] as LessonOverride[]).forEach((o) => {
    map.set(o.id, o);
  });
  return Array.from(map.values());
}

/** 특정 학생·날짜(원래 날짜 기준)의 변경을 불러와요. */
export async function fetchOverride(
  studentId: string,
  date: string,
): Promise<LessonOverride | null> {
  const { data, error } = await supabase
    .from('lesson_overrides')
    .select('*')
    .eq('student_id', studentId)
    .eq('override_date', date)
    .maybeSingle();
  if (error) throw error;
  return (data as LessonOverride) ?? null;
}

export interface OverrideContext {
  /** 이 날짜의 수업이 변경/이동/휴강된 경우 */
  out: LessonOverride | null;
  /** 이 날짜로 옮겨온 보충 수업들 */
  movedIn: LessonOverride[];
}

/** 특정 학생·날짜의 변경 맥락(그 날 변경 + 이 날로 옮겨온 보충)을 불러와요. */
export async function fetchOverrideContext(
  studentId: string,
  date: string,
): Promise<OverrideContext> {
  const { data, error } = await supabase
    .from('lesson_overrides')
    .select('*')
    .eq('student_id', studentId)
    .or(`override_date.eq.${date},moved_to_date.eq.${date}`);
  if (error) throw error;
  const rows = (data as LessonOverride[]) ?? [];
  return {
    out: rows.find((o) => o.override_date === date) ?? null,
    movedIn: rows.filter((o) => o.kind === 'move' && o.moved_to_date === date),
  };
}

/** 수업 변경을 저장(있으면 갱신)해요. */
export async function saveOverride(input: OverrideInput): Promise<LessonOverride | null> {
  const { data, error } = await supabase
    .from('lesson_overrides')
    .upsert(
      {
        student_id: input.studentId,
        override_date: input.date,
        kind: input.kind,
        start_time: input.start ?? '',
        end_time: input.end ?? '',
        moved_to_date: input.kind === 'move' ? input.movedToDate ?? null : null,
        note: (input.note ?? '').trim() || null,
      },
      { onConflict: 'student_id,override_date' },
    )
    .select()
    .maybeSingle();
  if (error) throw error;
  return (data as LessonOverride) ?? null;
}

/** 특정 학생·날짜의 변경을 삭제(정규 수업으로 되돌림)해요. */
export async function deleteOverride(studentId: string, date: string): Promise<void> {
  const { error } = await supabase
    .from('lesson_overrides')
    .delete()
    .eq('student_id', studentId)
    .eq('override_date', date);
  if (error) throw error;
}